import { useQuery } from 'react-query';

import { useRequest, type QueryFunction } from '@frontend/api';

/**
 * Shape of a task as returned by the dashboard endpoint.
 * Mirrors the TaskListSerializer response (same as the existing tasks list API).
 */
export type TaskDashboardItem = {
  id: number;
  status: string;
  dueDate: string | null;
  resolvedDate: string | null;
  assignedToId: number;
  assignedToFullName: string;
  createdById: number;
  createdByFullName: string;
  groupId: number;
  part: {
    uuid: string;
    partNumber: string;
    revision: string;
    filename: string;
    primaryFileUuid: string;
    containsVirus: boolean;
  } | null;
  annotation: {
    id: number;
    comments: { content: string }[];
    commentedOnQuoteItemId: number | null;
    commentedOnQuoteNumberWithRevision: string | null;
  } | null;
  quoteItemComponents: {
    quoteItemId: number;
    quoteNumberWithRevision: string;
  }[];
};

type TasksDashboardResponse = {
  results: TaskDashboardItem[];
  count: number;
};

type TasksDashboardQueryParams = {
  page: number;
  pageSize: number;
  assignedToIn?: number[];
  statusIn?: string[];
  groups?: number[];
};

export const getUseGetTasksDashboardKey = (params: TasksDashboardQueryParams) =>
  ['tasks', 'dashboard'].concat([JSON.stringify(params)]);

export const useGetTasksDashboard: QueryFunction<
  TasksDashboardResponse,
  [TasksDashboardQueryParams],
  ReturnType<typeof getUseGetTasksDashboardKey>
> = (params, { queryOptions, requestOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    queryKey: getUseGetTasksDashboardKey(params),
    queryFn: ({ signal }) =>
      request<TasksDashboardResponse>('/part_library/tasks/dashboard', {
        query: params,
        init: { signal },
      }),
  });
};
